import axios from "axios";
import {
  SET_FACTORY_FOR_USER,
  SET_QUERY_PARAMS,
  SET_USER_DETAILS,
  SET_EXISTING_JSON,
  UPDATE_LCL_SHIPMENT,
  SET_DELIVERY_SELECTED_EXISTING_FACTORY_NEW,
  UPDATE_AGENT_DETAILS,
  SET_COMMODITY_CATEGORY_LIST,
  INSURANCE_AUTH_TOKEN
} from "../actions/types";

import { updateErrorAndLoading } from "../actions";
import { makeGCPRequest } from "./getSellingRates";
import { getActivityContext } from "../util/activitySession";
import { setting_JSON_For_Redux } from "./settingReduxStore";
import { setOverlayProgress } from "../actions/code_config_actions";
import { isDevENV } from "../util/isDevENV";
import { filterRatesWithoutSchedules } from '../util/filterRatesWithoutSchedules';

/**
 * Map a single rate into rates_from_bb with per-rate failure isolation. A throw while mapping one
 * rate must NOT abort the whole batch — otherwise a single malformed rate (e.g. an LCL rate with a
 * null via_port) makes every rate for the lane silently disappear. Shared by get_Client_Rate and
 * get_buying_rate so the isolation lives in exactly one place.
 */
const mapRateIntoStore = async (getstate, rates_object, rates_from_bb, pos, query_parameter) => {
  if (!rates_object || rates_object.carrier === null) return;
  try {
    await setting_JSON_For_Redux(
      getstate,
      rates_object,
      rates_from_bb,
      pos,
      query_parameter,
      true,
      rates_object.quote
    );
  } catch (rateErr) {
    // Log the full error (type + stack), not just the message, so prod failures are debuggable.
    console.error(`Skipping rate at pos ${pos} (id=${rates_object?.id}):`, rateErr);
  }
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_G_RECAPTCHA_SITE_KEY
const URL = window.location.href;
let LINK = URL.includes("primefreight.com");

if (URL.includes("localhost") || URL.includes("netlify.app")) {
  LINK = false;
}
// When REACT_APP_PROXY_BETTY=YES, route all Betty GraphQL through the same-origin
// /api/betty function (forwards server-side). Same-origin → no CORS, so the dev heroku
// proxy isn't needed in that mode either. Otherwise use the direct/dev-proxy URL as before.
export const BASE_API =
  process.env.REACT_APP_PROXY_BETTY === "YES"
    ? "/api/betty"
    : LINK === false
      ? "https://primefreight-portal-dev.herokuapp.com/proxy-request?url=https://primefreight.betty.app/api/runtime/da93364a26fb4eeb9e56351ecec79abb"
      : "https://primefreight.betty.app/api/runtime/da93364a26fb4eeb9e56351ecec79abb";

// HEAVY BB actions (create booking, insurance token/quote/policy, FCL drayage) go to
// rate-api's allowlisted /betty-action passthrough on Cloud Run (300s ceiling) instead of
// the /api/betty Netlify function (killed at ~10–26s — the same cap that broke rate
// search). Same request/response shape as BASE_API; the anonymous 'pf-anon' placeholder
// is swapped for a real JWT server-side there too. Fast reads stay on BASE_API.
export const BETTY_ACTION_API =
  "https://rate-api-462238530065.us-central1.run.app/betty-action";

// LEGACY: old new-gen low-code action (action_id 346d9044…). Replaced by the
// BB custom function action (sendCreateBooking via Create_Quote_And_Booking).
// May be removed once the new booking flow is stable end-to-end.
export const sendBookingData = async (
  jwt_token,
  bookingData,
  booking_charges
) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  let query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id:"346d9044bf2f47bb82881932ca342a39",
      input: {
        AgentProfileId: bookingData?.agentProfileID,
        style_nuumbers: booking_charges?.style_nuumbers,
        po_numbers: booking_charges?.po_number,
        pickup_section: booking_charges?.pickup_section,
        ers_section: booking_charges?.ers_section,
        booking: bookingData,
        port_origin_section: booking_charges?.port_origin_section,
        port_discharge_section: booking_charges?.port_discharge_section,
        delivery_section: booking_charges?.delivery_section,
        delivery_section_charges: booking_charges?.delivery_section_charges,
        discountID: booking_charges?.discountID,
        pickup_section_charges: booking_charges?.pickup_section_charges,
        insuranceArr: booking_charges?.insurances,
        freight_section_charges: booking_charges?.freight_section_charges
      },
    },
  };

  return axios.post(BASE_API, query, { headers }).then((response) => {
    if (response?.data?.data?.action?.results) {
      return [true, response?.data?.data?.action?.results];
    }
    return [false];
  });
};

export const sendNewShipperData = async (
  jwt_token,
  newShipper,
  isNewShipper
) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  let query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id: "b0303eb5cd0c4ae59ca80f7c827638a9",
      input: {
        isNewShipper: isNewShipper,
        shipper_id: newShipper.id,
        country: newShipper.country,
        city: newShipper.city,
        port: newShipper.port.id,
        address: newShipper.address,
        business_name: newShipper.name,
        business_type: newShipper.business_type,
        contact_name: newShipper.contact_name,
        email: newShipper.email,
        postal: newShipper.postal,
        telephone_no: newShipper.phone_number,
      },
    },
  };

  return axios.post(BASE_API, query, { headers }).then((response) => {
    if (response.status === 200 || response.status === 201) {
      return {
        status: response.status,
        data: response.data,
      };
    } else {
      return {
        status: response.status,
        error: response.statusText,
      };
    }
  });
};

export const sendNewDeliveryLocationData = async (
  jwt_token,
  newDeliveryLocation,
  isNewDeliveryLoc
) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  let query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id: "f2ce28eaf462451c812efc6c88166938",
      input: {
        isNewLocation: isNewDeliveryLoc,
        location_id: newDeliveryLocation.id,
        country: newDeliveryLocation.country,
        city: newDeliveryLocation.city,
        destination_city: newDeliveryLocation.port.id,
        address: newDeliveryLocation.address,
        business_name: newDeliveryLocation.name,
        business_type: newDeliveryLocation.business_type,
        contact_name: newDeliveryLocation.contact_name,
        email: newDeliveryLocation.email,
        postal: newDeliveryLocation.postal,
        phone_no: newDeliveryLocation.phone_number,
      },
    },
  };

  return axios.post(BASE_API, query, { headers }).then((response) => {
    if (response.status === 200 || response.status === 201) {
      return {
        status: response.status,
        data: response.data,
      };
    } else {
      return {
        status: response.status,
        error: response.statusText,
      };
    }
  });
};

export const sendbookingDataFromLandingPage = async (
  jwt_token,
  bookingObject,
  newBooking,
  form1,
  form2,
  insuranceArr,
  dutyTax
) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  let query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id: "1bdf6ca2b7f84cd0bd341ec054290dc5",
      input: {
        pickup_section: bookingObject?.pickup_section,
        new_booking_form_info: newBooking,
        port_discharge_section:
          bookingObject?.port_discharge_section,
        port_origin_section: bookingObject?.port_origin_section,
        delivery_section_rate:
          bookingObject?.delivery_section_rate,
        booking_object: bookingObject?.booking_data,
        delivery_section: bookingObject?.delivery_section,
        ers_section: bookingObject?.ers_section,
        shipper_receiver_form_1: form1,
		    shipper_receiver_form_2: form2,
        insuranceArr: insuranceArr,
        dutyAndTax: dutyTax,
        isDev: isDevENV() //set true to test in development
      },
    },
  };
  return axios.post(BASE_API, query, { headers }).then((response) => {
    if (!response?.data?.data?.errors || response?.data?.data?.action) {
      return true;
    }
    return false;
  });
};


export const sendBookingThroughAgent = async (jwt_token,customerForm, shipperForm, formInfo,selectedAgent) =>{
  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }

      let query = {
          "query": "mutation { action(id: $action_id input: $input)}",
          "variables": {
              "action_id": "7512761b97ec4fb3b096725f41089610",
              "input": {
                 "customerForm": customerForm,
                 "shipperReceiverForm":shipperForm,
                 "formInfo":formInfo,
                 "agent": selectedAgent,
                 "isDev": isDevENV() //set true to test in development
              }
          }
      }

      return axios.post(BASE_API, (query), { headers }).then(response => {
          if (response?.data?.data?.action){
              return true
          }
          return false
      })
}

export const sendNewSignUpRequest = async (jwt_token,form1,form2,bookform,bookingDatacharges) =>{
  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }

      let query = {
          "query": "mutation { action(id: $action_id input: $input)}",
          "variables": {
             "action_id": "a65a99e584c746bf8134f707d3ed435c",
              "input": {
                "bookform": bookform,
                "bookingDatacharges": bookingDatacharges,
                "signUpForm1": form1,
                "signUpForm2": form2,
                "isDev": isDevENV(), //set true to test in development
              }
          }
      }
      return axios.post(BASE_API, (query), { headers }).then(response => {
        if (response?.data?.data?.action?.results){
              return [true, response?.data?.data?.action.results]
          }
          return false
      })
}

// pf-admin-write action UUID in the da93364a runtime zone (same id C uses as
// _BB_ACTION_ADMINWRITE_ID). The coded saveQuote op lives here; the frontend calls
// it directly via the standard { op, payload } single-JSON handler.
const PF_ADMIN_WRITE_ACTION_ID = "5daf66bed6e949f1b3b1f2d3bcc318f8";

/**
 * Save a quote via the CODED saveQuote op (replaces the legacy low-code 4c86ee89).
 * `payload` is the { quote, checked, charges, insuranceQuote } structure built by
 * util/saveQuotePayload.buildSaveQuotePayload. Returns { ok, quoteId } / { ok:false, error }.
 * BB exposes a custom action's output under `action.results` (plural — same as every
 * other helper here and the legacy save-quote). The pf-admin-write handler returns
 * `{ ok, op, result:{ quoteId } }` (JSON-stringified) in that field, so read
 * `action.results` then parse the inner envelope for { quoteId }.
 */
export const sendSaveQuote = async (jwt_token, payload) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  const query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id: PF_ADMIN_WRITE_ACTION_ID,
      input: { op: "saveQuote", payload: JSON.stringify(payload) },
    },
  };
  try {
    const response = await axios.post(BASE_API, query, { headers });
    const action = response?.data?.data?.action;
    const raw = action?.results ?? action?.result;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed?.ok) return { ok: true, quoteId: parsed.result?.quoteId };
    return { ok: false, error: parsed || response?.data?.errors || "no result" };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
};

// Create the Booking via the coded create-booking BB function (tracker #197 / decision
// #106 B2). It does the BOOKING half only — the Quote must already exist (saveQuote),
// so the caller passes its quoteId in the single JSON payload
// { booking, quoteId, bookingInsurance } (Neil's convention — parsed inside the function).
// Returns { ok, bookingId, bookingNumber } from the handler envelope (action.results).
// FLAG(neil-verify): action id must point at the PUBLISHED create-booking action (same id
// the existing saveBookingDataTest test sender used).
const CREATE_BOOKING_ACTION_ID = "2266d1b28f2b4c66bc903c3ab60c48c1";
export const sendCreateBooking = async (jwt_token, payload) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  const payloadJson = JSON.stringify(payload);
  // Confirm the FE actually sends a non-empty payload (the action log showed test:'' →
  // empty payload reaching BB). If this prints full JSON but the action still logs empty,
  // the mismatch is on the BB side (action id / input variable name), not here.
  // console.log("----create booking payload----", payloadJson);
  const query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id: CREATE_BOOKING_ACTION_ID,
      input: { payload: payloadJson },
    },
  };
  try {
    const response = await axios.post(BETTY_ACTION_API, query, { headers });
    const action = response?.data?.data?.action;
    const raw = action?.results ?? action?.result;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed?.ok) {
      return {
        ok: true,
        bookingId: parsed.result?.bookingId,
        bookingNumber: parsed.result?.bookingNumber,
        quoteId: parsed.result?.quoteId,
      };
    }
    return { ok: false, error: parsed || response?.data?.errors || "no result" };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
};

// Public booking no longer posts to Betty from the browser: the one-page
// Confirm & book goes through the pf-comms PROXY (util/rateGateApi.submitBooking
// → POST /rate-gate/booking), which holds the reCAPTCHA secret + the non-public
// wrapper-action id server-side. Nothing Betty-shaped ships to the client.


const executeReCaptcha = async () => {
  try {
      const resCode = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY);
      if (!resCode) {
          throw new Error("reCAPTCHA token is invalid.");
      }
      return resCode;
  } catch (error) {
      console.error("Error during reCAPTCHA execution:", error);
      throw new Error("reCAPTCHA execution failed");
  }
};

// activity-crm PR-2c: saveLogActivityData (the two legacy client-side SEARCH
// logging BB actions — logged-in 6cc049db… / guest a0e2352…) was removed here.
// SEARCH is now logged server-side by rate-api from the /rate-search payload's
// `activity` block, into the BB Ratesearchactivity event stream (decision D4).

export function searchPOs(jwt_token, searchText) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  const query = `{
        allPurchaseOrder(where: {orderNumbers: {matches: "${searchText}"}}) {
          results {
            orderNumbers
            id
            styleNo
          }
          totalCount
        }
      }`;

  return axios.post(BASE_API, { query }, { headers }).then((result) => {
    if (result?.data?.data?.allPurchaseOrder.results === undefined) {
      return [];
    }

    let resultPO = result?.data?.data?.allPurchaseOrder.results;
    if (resultPO) {
      return resultPO;
    } else {
      return [];
    }
  });
}

// Like searchPOs, but also selects the PO's commodity fields so a picked PO can
// populate the customs + insurance commodity rows (#179). A PurchaseOrder carries
// one item (itemDescription + valueOfGoodsUsd + originCountry) — no HS code (that
// is looked up from the description). Matches on orderNumbers, like searchPOs.
export function searchPurchaseOrdersDetailed(jwt_token, searchText) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  const query = `{
        allPurchaseOrder(where: {orderNumbers: {matches: "${searchText}"}}) {
          results {
            id
            orderNumbers
            styleNo
            itemDescription
            valueOfGoodsUsd
            originCountry
            currency
          }
          totalCount
        }
      }`;

  return axios.post(BASE_API, { query }, { headers }).then((result) => {
    return result?.data?.data?.allPurchaseOrder?.results ?? [];
  });
}

export function getPoNumberBySearch(jwt_token) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  let query = `{allBooking
            {results
             {  
                id
                posText
                pos{
                    id
                    orderNumbers
                }
                styleNumbers
             }
            }
          }`;

  return axios.post(BASE_API, { query }, { headers }).then((result) => {
    let allPurchaseOrders =
        result?.data &&
        result?.data?.data &&
        result?.data?.data?.allBooking &&
        result.data.data.allBooking.results
          ? result.data.data.allBooking.results
          : [],
      allPoNumber = [];

    allPurchaseOrders.forEach((tempPurchaseOrder) => {
      if (
        tempPurchaseOrder &&
        tempPurchaseOrder.posText &&
        tempPurchaseOrder.pos.length > 0 &&
        !Object.keys(allPoNumber)
          .map((data) => allPoNumber[data].orderNumbers)
          .includes(tempPurchaseOrder.posText)
      ) {
        allPoNumber.push({
          orderNumbers: tempPurchaseOrder.pos[0].orderNumbers,
          id: tempPurchaseOrder.pos[0].id,
          styleNumber: tempPurchaseOrder.styleNumbers,
        });
      }
    });
    return allPoNumber;
  });
}

export function addPurchaseOrder(jwt_token, payload) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  let query = {
    query: "mutation { action(id: $action_id input: $input)}",
    variables: {
      action_id: "8be86a3553094977ae45fba8f9f32b92",
      input: {
        payload: payload,
      },
    },
  };

  return axios
    .post(BASE_API, JSON.stringify(query), { headers })
    .then((result) => {
      if (result?.data?.data?.action?.results?.id) {
        return [true, result.data.data.action.results.id];
      }

      return [false];
    });
}


export const getAgentProfilesData = async (isUserPresent, getstate) =>{
  let jwt_token;
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
  }
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  const actionId = (isUserPresent)
  ? "f137ba3d2b764bbeb264402771765928" 
  : "dc114ea2a2134cdd960895c950f2c412";

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input:""
    },
  };

  return axios
    .post(BASE_API, JSON.stringify(query), { headers })
    .then((result) => {
      if (result?.data?.data?.action?.results) {
        return [true, result.data.data.action.results];
      }
      return [false];
  });
}

export const getHSCode = async (payload, getstate, dispatch) =>{
  const isUserPresent =  getstate()?.route?.USER_DETAILS?.webuser?.userid != null

  let jwt_token;
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  const actionId = isUserPresent 
  ? "26197fa8b7c048f58e799c029e4cedde"
  : "9299486a3344424aa20b0a5e3ec2ce48"

  const input = {
    "payload": payload
  }

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input,
    },
  };
   
  return axios.post(BASE_API, (query), { headers }).then(response => {
    if (response?.data?.data?.action?.results){
      return [true, response?.data?.data?.action?.results]
    }else if(response?.data?.data?.errors && response.data.data.errors.length > 0){
      const error = response.data.data.errors[0];  // Get the first error
      const errorMessage = error.message;  // Extract the error message
      const errorCode = error.extensions.statusCode;
      dispatch(updateErrorAndLoading(`${errorCode} : ${errorMessage}`, undefined));
    }
    return false

  })
}


export const getDutyAndTaxFromSimplyDuty = async (payload, getstate, dispatch) =>{
  const isUserPresent =  getstate()?.route?.USER_DETAILS?.webuser?.userid != null

  let jwt_token;
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };
  const actionId = isUserPresent 
  ? "5ff3c5e1c01949378f1a91f53a8f5996"
  : "d0712405ee6d4b0289d6b44d99056ae4";

  const input = {
    "payload": payload
  }

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input,
    },
  };
   
  return axios.post(BASE_API, (query), { headers }).then(response => {
      if (response?.data?.data?.action?.results){
        return [true, response?.data?.data?.action?.results]
      }else if(response?.data?.data?.errors && response.data.data.errors.length > 0){
        const error = response.data.data.errors[0];  // Get the first error
        const errorMessage = error.message;  // Extract the error message
        const errorCode = error.extensions.statusCode; //
        dispatch(updateErrorAndLoading(`${errorCode} : ${errorMessage}`, undefined));
      }
      return false

  })
}


export const getSelectedCustomBroker = async (jwt_token, userId) =>{
  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }

  let query = `{
  allCompany(
      where: {
          id: { eq: ${userId}}
      }
  ) {
      results {
          id
          companyName
          customBroker{
              id
              companyName
          }
      }
      totalCount
  }
}`

return axios.post(BASE_API, { query }, { headers }).then(result => {
  if (result.data.data && result.data.data.allCompany) {
      const responseData = result.data && result.data.data && result.data.data.allCompany.results ? result.data.data.allCompany.results : []
      return [200, responseData];
  }
  return [403,[]]
})
}



export const getAllCustomBroker = async (jwt_token) =>{
  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }

  let query = `{
  allCompany(
      where: {
          isCustomBroker: { eq: true }
          companyName: { regex: "CB" }
      }
  ) {
      results {
          id
          companyName
      }
      totalCount
  }
}`


return axios.post(BASE_API, { query }, { headers }).then(result => {
  if (result.data.data && result.data.data.allCompany) {
      const responseData = result.data && result.data.data && result.data.data.allCompany.results ? result.data.data.allCompany.results : []
      return [200, responseData];
  }
  return [403,[]]
})
}

export function getInsuranceAPItoken(jwt_token, isUserPresent) {

  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }
  
  const actionId = isUserPresent 
  ? "813afe45f26f4076b1459ef9848782cd" 
  : "4e4ad81d06e54ed99a6115b457dc2fd9"; 

  const input = {
    "isDev": isDevENV() //set true to test in development
  }

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input
    },
  };

  return axios.post(BETTY_ACTION_API, JSON.stringify(query), { headers }).then(result => {
    if (result?.data?.data?.action?.results) {
          return [true, result.data.data.action.results];
      }
      return [false];
  })
}

export function getCargoInsurance(isUserPresent, insuranceAuthToken, payload, customer, getstate) {
  let jwt_token;
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
    //console.log("web user: ",jwt_token)
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
    //console.log("public user: ",jwt_token)
  }

  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }

  const actionId = isUserPresent 
  ? "3fa41ca362c346a6959d237d39fe169f" 
  : "b25171b4197248368265d3d35af0c9eb";

  const input = {
      "token": insuranceAuthToken,
      "payload": payload,
      "isDev": isDevENV() //set true to test in development
  };

  if (!isUserPresent) {
      input.customer = customer
  }

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input,
    },
  };

  return axios.post(BETTY_ACTION_API, JSON.stringify(query), { headers }).then(result => {
      if (result?.data?.data?.action?.results) {
          return [true, result.data.data.action.results];
      }else if(result?.data?.data?.errors) {
        const errMsg = result.data.data.errors[0].message
        console.error(errMsg)
      }
      return [false];
  })
}

export function getCargoInsurancePolicy(isUserPresent, insuranceAuthToken, quoteId, getstate) {
  let jwt_token;
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
  }

  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }
  const actionId = isUserPresent 
  ? "db00c91df987431e85ab2b4e3131507e" 
  : "b093c1b947c7405483f54ff00de5578a";

  const input = {
      "token": insuranceAuthToken,
      "quoteId": quoteId,
      "isDev": isDevENV() //set true to test in development
  };

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input,
    },
  };

  return axios.post(BETTY_ACTION_API, JSON.stringify(query), { headers }).then(result => {
      if (result?.data?.data?.action?.results) {
          return [true, result.data.data.action.results];
      }else if(result?.data?.data?.errors) {
        const errMsg = result.data.data.errors[0].message
        console.error(errMsg)
      }

      return [false];

  })
}

export function getCommodityList(jwt_token,isUserPresent,insuranceAuthToken) {
  const headers = {
      'Content-Type': 'application/json',
      'Authorization': "Bearer " + jwt_token + ""
  }
    
  const actionId = isUserPresent 
  ? "d1b6ffa9b68640a0b01893e7390a2cbf"
  : "3619852af1c342d59f5620657a9fc77d";

  const input = {
    "token": insuranceAuthToken,
    "isDev": isDevENV() //set true to test in development
  };

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
        action_id: actionId,
        input,
    },
  };

  return axios.post(BETTY_ACTION_API, JSON.stringify(query), { headers }).then(result => {
      if (result?.data?.data?.action?.results) {
          return [true, result.data.data.action.results];
      }
      return [false];
  })
}


export const get_commodity_list = async (getstate,dispatch) => {
  const isUserPresent =  getstate()?.route?.USER_DETAILS?.webuser?.userid != null
  let jwt_token
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
  }

    // Step 1: Get insurance API token
    getInsuranceAPItoken(jwt_token, isUserPresent)
        .then(([success, results]) => {
            if (success) {
                // Step 2: Use the token to get the commodity list
                const insuranceAuthToken = "Bearer "+results; // Adjust based on the actual token format
                dispatch({
                  type: INSURANCE_AUTH_TOKEN,
                  payload: insuranceAuthToken,
              });
                return getCommodityList(jwt_token, isUserPresent, insuranceAuthToken);
            } else {
                throw new Error('Failed to fetch insurance API token');
            }
        })

        .then(([success, commodityResults]) => {
            if (success) {
                //console.log("\ncommodityResults: ",commodityResults)
                dispatch({
                  type: SET_COMMODITY_CATEGORY_LIST,
                  payload: commodityResults,
              });
                return success
            } else {
                throw new Error('Failed to fetch commodity list');
            }
        })
        .catch(error => {
          console.error(error)
        });
}

// export const getScheduleData = async (id, isUserPresent, getstate) => {
//   let jwt_token;
//   if(isUserPresent){
//     jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
//     //console.log("web user: ",jwt_token)
//   }else{
//     jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
//     //console.log("public user: ",jwt_token)
//   }

//   const headers = {
//       "Content-Type": "application/json",
//       Authorization: "Bearer " + jwt_token + "",
//     };


//   const actionId = isUserPresent 
//   ? "c6c6b0968ead40e3bee24ebbff6589d2" 
//   : "2709b645e2c346b091e097b8787aab6c";

//   const input = {
//     "charge_type_id": id
//   };
//   // if (!isUserPresent) {
//   //   try {
//   //       input.token = await executeReCaptcha();
//   //   } catch (error) {
//   //       return [500, error.message];
//   //   }
//   // }

//   const query = {
//     query: "mutation { action(id: $action_id input: $input) }",
//     variables: {
//         action_id: actionId,
//         input,
//     },
//   };

//   return axios.post(BASE_API,  query , { headers }).then((result) => {
//     if (result.data.data && result.data.data.action.results) {
//       const responseData =
//       result.data &&
//       result.data.data &&
//       result.data.data.action.results
//         ? result.data.data.action.results?.[0]
//         : [];

//       let scheduleData = [];
//       if (responseData) {
//           //const scheduleDataResult = responseData.buying_rate ? responseData.schedules : responseData?.buying_linked_rate?.schedules;
//           //console.log("scheduleDataResult", scheduleDataResult);
//           const scheduleDataResult =  responseData?.schedules
//           if(scheduleDataResult && scheduleDataResult.length > 0){
//             scheduleData = scheduleDataResult
//           }
//           return [200, scheduleData];
//       } else {
//           return [403, []];
//       }
//   }
//   return [403, []];
// });
// }

/**
 * @function
 * @name Make_Database_Call
 *@description will make call to database via post
 * @return return response from API
 *
 */
const Make_Database_Call = async (jwt_token, query) => {
  if (LINK) {
    return await axios
      .post(
        BASE_API,
        {
          query: query,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt_token + "",
          },
        }
      )
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          return {
            status: response.status,
            data: response.data,
          };
        } else {
          return {
            status: response.status,
            error: response.statusText,
          };
        }
      });
  } 
  else {
        return await axios
      .post(
        BASE_API,
        {
          query: query,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt_token + "",
          },
        }
      )
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          return {
            status: response.status,
            data: response.data,
          };
        } else {
          return {
            status: response.status,
            error: response.statusText,
          };
        }
      });
  }
};

/**
 * @function
 * @name all_response_validation
 * @description will check the response from api till grapqhl, return true, if all success, else return false if there is any error
 * @return true if sucess, else false with fail message
 * @param response
 * @return {Promise<{bool: boolean, error}>}
 */
export const all_response_validation = async (response, property) => {
  if (response.status === 200 || response.status === 201) {
    // when grapqhl response with data
    if (response?.data?.data?.hasOwnProperty(property)) {
      return {
        bool: true,
        response: response,
      };
    }
    // grapqhl ql error
    else {
      return {
        status: "520",
        bool: false,
        error: "Token Expire, Sign-out and Sign-in again",
      };
    }
  }
  // when response does not success
  else {
    return {
      status: response.status,
      bool: false,
      error: response.error,
    };
  }
};

export const change_lcl_vessel_service = async (rates_object) => {
  if (rates_object.rate_type === "LCL") {
    rates_object.vessel_service = {
      name: "Prime freight service contract",
      // "id": rates_object.carrier.id
      id: 307,
    };
  }

  return rates_object;
};

export const Set_Query_For_Client = async (query, query_parameter, Type) => {
  query += "{\n";
  // To get the client detail from Database
  if (Type === "DETAILS") {
    query += "meWebuser\n";
    query += "{\n";
    query += "id\n";
    query += "email\n";
    query += "firstName\n";
    query += "lastName\n";
    query += "entity";
    query += "{";
    query += "id\n";
    query += "companyName\n";
    query +=
      "rateProfile{\n"+
      "id \n"+
      "minimumCbm \n"+
      "}\n";
    query += "}";
    query += "}";
  }
  query += "}";

  return query;
};

/**
 * @function
 * @name de_duplicates_rates
 * @description it will de-duplicate rates based on rates, location_from,location_to, vessel_service, created at
 * @return collection of rates
 * @param rates_from_graphql
 */

export const update_vessel_Service_vendor_contract = async (
  rate_object,
  vessel
) => {
  if (vessel === true) {
    rate_object.vessel_service = {
      id: rate_object.carrier.id,
      name: rate_object.carrier.company_name,
    };
  } else {
    rate_object.vendor_contract = {
      id: rate_object.carrier.id,
      name: rate_object.carrier.company_name,
    };
  }
};

export const de_duplicates_rates = async (rates_from_graphql, type, requestFromClient) => {
  // console.log("rates_from_graphql.length: ", rates_from_graphql.length);
  const final_rates = [];
  // helper to rank externalRateType
  const priority = { spotOn: 3, contract: 2, public: 1 };
  // Loop through each rate
  for (let pos = 0; pos < rates_from_graphql.length; pos++) {
    const currentRate = rates_from_graphql[pos];
    // Handle null vessel_service and vendor_contract
    if (currentRate.vessel_service === null) {
      await update_vessel_Service_vendor_contract(currentRate, true);
    }

    if (currentRate.vendor_contract === null) {
      await update_vessel_Service_vendor_contract(currentRate, false);
    }

    if (pos === 0) {
      final_rates.push(currentRate); // Add first rate to final_rates
    } else {
      let isDuplicateFound = false;
      // Loop through final_rates to check for duplicates
      for (let second_pos = 0; second_pos < final_rates.length; second_pos++) {
        const existingRate = final_rates[second_pos];
        // Handle null vessel_service and vendor_contract for existing rates
        if (existingRate.vessel_service === null) {
          await update_vessel_Service_vendor_contract(existingRate, true);
        }
        if (existingRate.vendor_contract === null) {
          await update_vessel_Service_vendor_contract(existingRate, false);
        }
        // Check if the rates have the same important properties
        if (
          currentRate.location_from.id === existingRate.location_from.id &&
          currentRate.location_to.id === existingRate.location_to.id &&
          currentRate.via_port.id === existingRate.via_port.id &&
          currentRate.carrier.id === existingRate.carrier.id &&
          currentRate.id !== existingRate.id && // Exclude comparing the same rate
          // currentRate.vessel_service.id === existingRate.vessel_service.id &&
          (requestFromClient === false ? rates_from_graphql[pos].vessel_service.id === final_rates[second_pos].vessel_service.id : true) &&
          currentRate.vendor_contract.id === existingRate.vendor_contract.id
        ) {
          // Check if transshipment arrays have the same length
          const currentTransshipments = currentRate.transshipments || [];
          const existingTransshipments = existingRate.transshipments || [];
          // Check if lengths are the same and port_of_transhipment matches
          if (
            currentTransshipments.length === existingTransshipments.length &&
            currentTransshipments.every((currentTransshipment, index) =>
              currentTransshipment.port_of_transhipment.id === existingTransshipments[index].port_of_transhipment.id
            )
          ) {

            isDuplicateFound = true;
            // Compare by cost if specified
            if (type === "LCL") {
              const currentUnitCost = parseFloat(currentRate.unit_cost || "0");
              const existingUnitCost = parseFloat(existingRate.unit_cost || "0");
              // console.log("currentUnitCost: ",currentUnitCost,"\nexistingUnitCost: ",existingUnitCost)
              // Keep the rate with the cheaper unit cost
              if (currentUnitCost < existingUnitCost) {
                final_rates[second_pos] = currentRate; // Replace with the cheaper rate
                // console.log(`Replaced rate with id ${existingRate.id} by cheaper rate with id ${currentRate.id}`);
              }

            } else {
              // —— NEW FCL logic —— 
              // priority order: 40′, then 20′, then 45′
              const keys = ["40price","20price","45price"]
              let resolved = false;
              for (let k of keys) {
                const cVal = Number(currentRate[k]  || 0)
                const eVal = Number(existingRate[k] || 0)
                if (cVal < eVal) {
                  final_rates[second_pos] = currentRate
                  resolved = true;
                  break   // current is cheaper → replace & stop
                }
                if (cVal > eVal) {
                   resolved = true;
                  break   // existing is cheaper → do nothing & stop
                }
              }
                // 2) if *still* tied on all three, break tie by rate‐type priority
              if (!resolved) {
                const currentRateType = currentRate?.external_rate_type;    // or external_rate_type
                const existingRateType = existingRate?.external_rate_type;
                if ((priority[currentRateType]||0) > (priority[existingRateType]||0)) {
                  final_rates[second_pos] = currentRate
                }
              }
            }
            break;
          }
        }
      }
      // If no duplicate was found, push the new rate
      if (!isDuplicateFound) {
        final_rates.push(currentRate);
      }
    }
  }

  //console.log("final_rates: ", final_rates);
  return final_rates;
};


export const Set_From_And_To_Parameter = (selectedPort) => {
  let linkedPort = selectedPort?.linkedPort;
  let portLinked = selectedPort?.portLinked;
  let linkedPortArray = [];

  if (linkedPort) {
    linkedPortArray = linkedPort.map((item) => ({
      id: item?.id,
      name: item?.companyName || item?.company_name,
      portCode: item?.scacCode || item?.scac_code
    }));
  }

  if (portLinked) {
    portLinked = {
      id: selectedPort?.portLinked?.id,
      name: selectedPort?.portLinked?.companyName || selectedPort?.portLinked?.company_name,
      portCode: selectedPort?.portLinked?.scac_code ||selectedPort?.portLinked?.scacCode
    };
  }

  const selectedLocation = {
    id: selectedPort?.id,
    name: selectedPort?.name,
    portCode: selectedPort?.portCode
  };

  const locationPortArray = [
    ...(linkedPortArray || []),
    ...(portLinked ? [portLinked] : []),
    ...(selectedLocation ? [selectedLocation] : []),
  ];

  const uniqueLocations = new Set();

  // Use filter to get unique records based on the 'id' property
  const uniqueLocationPortArray = locationPortArray.filter((record) => {
    if (!uniqueLocations.has(record.id)) {
      uniqueLocations.add(record.id);
      return true;
    }
    return false;
  });

  return uniqueLocationPortArray;
};

export const Set_Input_parameter = async (inputparam, query_parameter) => {
  query_parameter.location_from = Set_From_And_To_Parameter(
    inputparam?.form_params?.pickup?.selectedPort
  );

  query_parameter.location_to = Set_From_And_To_Parameter(
    inputparam?.form_params?.delivery?.selectedPort
  );

  query_parameter.fak_percentage = inputparam?.fakNacPercentage?.fakPercentage
  query_parameter.nac_percentage = inputparam?.fakNacPercentage?.nacPercentage

  query_parameter.etd_origin = inputparam?.form_params?.pickup?.date;
  if (inputparam.form_params.type === "api-lcl") {
    query_parameter.rate_type = "LCL";
    query_parameter.mode = "sea";
  } else if (inputparam.form_params.type === "api-fcl") {
    query_parameter.rate_type = "FCL";
    query_parameter.mode = "Sea";
  } else {
    query_parameter.mode = "Air";
  }
  let date = new Date(query_parameter.etd_origin);
  let year = date.toLocaleString("default", { year: "numeric" });
  let month = date.toLocaleString("default", { month: "2-digit" });
  let day = date.toLocaleString("default", { day: "2-digit" });
  query_parameter.etd_origin = year + "-" + month + "-" + day;
  query_parameter.etd_origin = query_parameter.etd_origin.toString();
};
/**
 * @function
 * @name get_Client_Rate
 * @param getstate -> to get the current state,
 * @param inputparam -> to get the input parameters from users
 * @param loadmore -> to loadmore grapql query
 * @param dispatch
 * @description To query buying rates, based on parameter, and environment
 * @return return object, containing rates with global profit
 */

export const set_query_to_get_factory_details = async (client_id, query) => {
  query += "{";
  query += "allCompany(";
  query += "where:{ _and : [ {id:{eq:" + client_id.id + "}} \n   ]}";
  query += ")";
  query += "{";
  query += "results";
  query += "{";
  query +=
    "id\n companyName\n knownShippers{ id\n nameAndAddress\n phoneNumber\n postalCode\n businessLocationType\n linkedportid\n  email\n companyName\n city\n country\n address_1\n address_2\n} location{ id\n nameAndAddress\n linkedPort{ id\n } businessLocationType\n phoneNumber\n postalCode\n city\n country\n email\n companyName\n address_1\n}";
  query += "}";
  query += "}";
  query += "}";

  return query;
};

export const map_loop_to_property = async (object, type) => {
  let new_object = {};

  new_object.address = object.address_1;
  new_object.address2 = object.address_2;
  new_object.business_type = object.businessLocationType;
  new_object.city = object.city;
  new_object.contact_name = object.companyName;
  new_object.country = object.country;
  new_object.email = object.email;
  new_object.id = object.id;
  new_object.name = object.nameAndAddress;
  new_object.phone_number = object.phoneNumber;
  new_object.postal = object.postalCode;
  new_object.isExistingFactorySelected = null;
  new_object.port = {};

  if (type === "PICK") {
    if (object.linkedPort !== undefined) {
      new_object.port.id = object.linkedPort.id;
    } else {
      new_object.port.id = null;
    }
  } else {
    if (object.linkedportid !== undefined) {
      new_object.port.id = object.linkedportid;
    }
    if (object.linkedPort === null) {
      new_object.port.id = null;
    }
  }

  return new_object;
};

export const set_properties_for_redux_store = async (user_data_for_form) => {
  let new_object = {};
  new_object.pickupExistingFactoryJson_old = user_data_for_form.knownShippers;
  new_object.deliveryExistingFactoryJson_old = user_data_for_form.location;
  new_object.pickupExistingFactoryJson = [];
  new_object.deliveryExistingFactoryJson = [];

  new_object.pickupExistingFactoryJson_old.map(async (value, index) => {
    new_object.pickupExistingFactoryJson.push(
      await map_loop_to_property(value, "PICK")
    );
  });

  new_object.deliveryExistingFactoryJson_old.map(async (value, index) => {
    new_object.deliveryExistingFactoryJson.push(
      await map_loop_to_property(value, "DEL")
    );
  });

  return new_object;
};

export const set_query_for_agent = async (param, query_param) => {
  query_param += "{";
  query_param +=
    "allCompany(" +
    "sort: { field: companyName, order: DESC }" +
    " where: {" +
    "_and: [" +
    '{entityType:{eq:"Country"}}' +
    '{companyName:{eq:"' +
    param.country +
    '"}}' +
    "]" +
    "}" +
    ")";
  query_param += "{";
  query_param += "results {";
  query_param += "companyName\n";
  query_param += "id\n";
  query_param += "linkedAgent{ id\n companyName\n email\n }";
  query_param += "}";
  query_param += "}";
  query_param += "}";

  return query_param;
};

export const get_Client_Rate = async (
  getstate,
  inputparam,
  loadmore,
  dispatch,
  set_client_only
) => {
  let query_parameter = {
    location_from: {},
    location_to: {},
    fak_percentage: "",
    nac_percentage: "",
    etd_origin: "",
    rate_type: "",
    mode: "",
    take: 100,
    skip: 0,
    user_data: {},
  };

  if (set_client_only === undefined) {
    await Set_Input_parameter(inputparam, query_parameter);
  }

  let jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  if (jwt_token !== "" || jwt_token !== undefined) {
    let customer_profile_query = "";
    let response = {};
    let status_object = {};

    //Saving Networks call, when log-in user load the portal, it already save his to store,
    //to Avoid getting details twice these functions are implementing
    customer_profile_query = await Set_Query_For_Client(
      customer_profile_query,
      query_parameter,
      "DETAILS"
    );
    if (Object.keys(getstate().route.USER_DETAILS).length === 0) {
      response = await Make_Database_Call(jwt_token, customer_profile_query);
      status_object = await all_response_validation(response, "meWebuser");
    } else {
      status_object.bool = true;
      status_object.response = {
        data: {
          data: {
            meWebuser: {
              id: getstate().route.USER_DETAILS.webuser,
              email: getstate().route.USER_DETAILS.webuser,
              firstName: getstate().route.USER_DETAILS.webuser,
              lastName: getstate().route.USER_DETAILS.webuser,
              entity: getstate().route.USER_DETAILS,
            },
          },
        },
      };
    }
    if (status_object.bool) {
      if (status_object.response.data.data.meWebuser.entity !== undefined) {
        if (Object.keys(getstate().route.USER_DETAILS).length === 0) {
          let user_data = status_object.response.data.data.meWebuser.entity;
          user_data.minimumCbm = user_data?.rateProfile?.minimumCbm
          user_data.webuser = {
            userid: status_object.response.data.data.meWebuser?.id,
            email: status_object.response.data.data.meWebuser?.email,
            firstName: status_object.response.data.data.meWebuser?.firstName,
            lastName: status_object.response.data.data.meWebuser?.lastName,
          };
          dispatch({
            type: SET_USER_DETAILS,
            payload: user_data,
          });

          if (user_data?.minimumCbm) {
            dispatch({
              type: UPDATE_LCL_SHIPMENT,
              payload: { totalVolume: user_data.minimumCbm },
            });
          }
          let query_for_user_details = "";
          query_for_user_details = await set_query_to_get_factory_details(
            user_data,
            query_for_user_details
          );
          let response_for_query = await Make_Database_Call(
            jwt_token,
            query_for_user_details
          );
          let status_object_for_user = await all_response_validation(
            response_for_query,
            "allCompany"
          );
          if (status_object_for_user.bool) {
            let user_data_for_form =
              status_object_for_user.response.data.data.allCompany.results[0];
            let change_properties = await set_properties_for_redux_store(
              user_data_for_form
            );
            dispatch({
              type: SET_EXISTING_JSON,
              payload: change_properties.pickupExistingFactoryJson,
            });

            dispatch({
              type: SET_DELIVERY_SELECTED_EXISTING_FACTORY_NEW,
              payload: change_properties.deliveryExistingFactoryJson,
            });
          } else {
            return {
              status: "404",
              error:
                "No User found, Error code 0012, please provide this id to prime freight ",
            };
          }
          // let get_know_shipper_and_factor_information_from_database=await set_query_for_user_details(user_data)
          // console.log(get_know_shipper_and_factor_information_from_database)
          if (set_client_only !== undefined) {
            return status_object;
          }
          query_parameter.user_data = user_data;
        } else {
          query_parameter.user_data = getstate().route.USER_DETAILS;
        }

        let query_for_agent_information = "";
        query_for_agent_information = await set_query_for_agent(
          getstate().route.queryParams.from,
          query_for_agent_information
        );
        let response_for_client_agent = await Make_Database_Call(
          getstate().route.CLIENT_TOKEN.JWT_TOKEN,
          query_for_agent_information
        );
        let status_object_for_agent = await all_response_validation(
          response_for_client_agent,
          "allCompany"
        );
        if (status_object_for_agent.bool) {
          let array_agents =
            status_object_for_agent.response.data.data.allCompany.results;
          if (array_agents.length > 0) {
            if (array_agents[0].linkedAgent !== null) {
              let final_object = {
                name: array_agents[0].linkedAgent.companyName,
                email: array_agents[0].linkedAgent.email,
                id: array_agents[0].linkedAgent.id,
              };
              dispatch({
                type: UPDATE_AGENT_DETAILS,
                payload: final_object,
              });
            }
          }
        }

        const originPort = inputparam?.form_params?.pickup?.selectedPort
        const destinationPort = inputparam?.form_params?.delivery?.selectedPort

        const payload = {
            origin:  originPort?.name || originPort?.portCode,
            destination: destinationPort?.name || destinationPort?.portCode,
            validFrom: query_parameter?.etd_origin,
            rateType: query_parameter?.rate_type,
            jwtToken: jwt_token,
            // activity-crm PR-2: attribution context for rate-api's server-side
            // SEARCH logging. rate-api ignores it if its log flag is off.
            activity: getActivityContext(),
        }
        // console.log("payload: ",payload);
        try{
          const rateData = await makeGCPRequest(payload);
          dispatch(setOverlayProgress(100));
          // console.log("---rates from GCP request------",rateData, rateData?.length)
          if (rateData && Array.isArray(rateData) && rateData.length > 0) {
            let rates_from_bb = {   
              show_blur_cards: false,
              rates: {
                fcl: [],
                lcl: [],
              },
            };

            //let rates_from_graphql = rateData;
            let rates_from_graphql = filterRatesWithoutSchedules(rateData);
            // console.log("rates_with_schedules: ",rates_from_graphql, rates_from_graphql?.length)
            for (let pos = 0; pos < rates_from_graphql.length; pos++) {
              await mapRateIntoStore(getstate, rates_from_graphql[pos], rates_from_bb, pos, query_parameter);
            }
            // console.log("rates_from_bb: ",rates_from_bb);
            return {
              status: "200",
              response: rates_from_bb,
            };
          } else {
            console.log("Rates are not available.");
            return {
              status: rateData?.status,
              error: "Rates are not available for the given port pair.",
            };
          }
      }catch(error){
        dispatch(setOverlayProgress(100));
        console.error("Failed to fetch rates from GCP:", error.message);
        throw error;
      }
      } else {
        return {
          status: "404",
          error:
            "No User found, Error code 0011, please provide this id to prime freight ",
        };
      }
    } else {
      return {
        status: status_object.status,
        error: status_object.error,
      };
    }
  } else {
    return {
      status: "404",
      error: "No JWT found",
    };
  }
};

// export const write_query_for_transit_time=(all_data,query)=>{
//
//     query+="{"
//     query+="allSchedule"
//     query+="("
//     query+="sort:{field:etd, order:DESC}"
//     query+="where:{"
//     query+="_and:["
//     query+='{origin:{companyName:{eq:"'+all_data.port_origin+'"}}}'
//     query+='{discharge:{companyName:{eq:"'+all_data.port_discharge+'"}}}'
//     query+='{carrier:{companyName:{eq:"'+all_data.carrier+'"}}}'
//     query+='{etd:{gteq:"'+all_data.etd_origin+'"}}'
//     query+="]"
//     query+="}"
//     query+=")"
//     query+="{"
//     query+="results{"
//     query+="id\n"
//     query+="voyage\n"
//     query+="transitTime\n"
//     query+="vesselService{ id name }"
//     query+="}"
//     query+="}"
//     query+="}"
//
//     return query
// }

// export const iterate_over_rates = async (rates)=>{
//     let all_data_for_transit={
//         "port_origin":rates.port_of_origin,
//         "port_discharge":rates.port_of_discharge,
//         "port_destination":rates.ers_location,
//         "carrier":rates.sealine,
//         "etd_origin":rates.etd_origin
//     }
//     let query=""
//     query=write_query_for_transit_time(all_data_for_transit,query)
//     console.log(query)
// }

/**
 * @function
 * @name add_transit_time
 * @param graphql_response-> get the flash card after response
 * @description to query the transit time for all the rates
 * @return return same set of flash card with transit time added if available on database
 */

// export const add_transit_time = async (graphql_response)=>{
//     let all_rates=graphql_response.response.rates
//    if(all_rates.fcl.length > 0){
//       all_rates.fcl.forEach((value, index)=>{
//           iterate_over_rates(value)
//       })
//    }else {
//        all_rates.lcl.forEach((value, index)=>{
//            iterate_over_rates(value)
//
//        })
//    }
//
//     return graphql_response
// }

/**
 * @function
 * @name get_buying_rate
 * @param getstate -> to get the current state,
 * @param inputparam -> to get the input parameters from users
 * @param loadmore -> to loadmore grapql query
 * @description To query buying rates, based on parameter, and environment
 * @return return object, containing rates with global profit
 */

export const get_buying_rate = async (getstate, inputparam, loadmore, dispatch, requestFromClient = false) => {
  let query_parameter = {
    location_from: {},
    location_to: {},
    etd_origin: "",
    rate_type: "",
    mode: "",
    take: 100,
    skip: 0,
  };
  // console.log("--input params: ",inputparam)
  await Set_Input_parameter(inputparam, query_parameter);
  // console.log("query_parameter: ",query_parameter, "\n getstate: ",getstate())
  const jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN;
  if (!jwt_token) {
    return { status: "404", error: "No JWT found" };
  }
  const originPort = inputparam?.form_params?.pickup?.selectedPort
  const destinationPort = inputparam?.form_params?.delivery?.selectedPort
  const discountCode = getstate().route?.queryParams?.discount

  const payload = {
      origin:  originPort?.name || originPort?.portCode,
      destination: destinationPort?.name || destinationPort?.portCode,
      validFrom: query_parameter?.etd_origin,
      rateType: query_parameter?.rate_type,
      discountCode: discountCode,
      jwtToken: jwt_token,
      // activity-crm PR-2: attribution context for rate-api's server-side
      // SEARCH logging. rate-api ignores it if its log flag is off.
      activity: getActivityContext(),
  }
  // console.log("payload: ",payload);
  try{
    const rateData = await makeGCPRequest(payload);
    dispatch(setOverlayProgress(100));
    // console.log("---rates from GCP request------",rateData)
    if (rateData && Array.isArray(rateData) && rateData.length > 0) {
      let rates_from_bb = {   
        show_blur_cards: false,
        rates: {
          fcl: [],
          lcl: [],
        },
      };

      let rates_from_graphql = filterRatesWithoutSchedules(rateData);
      // console.log("rates_with_schedules: ",rates_from_graphql)
      for (let pos = 0; pos < rates_from_graphql.length; pos++) {
        await mapRateIntoStore(getstate, rates_from_graphql[pos], rates_from_bb, pos, query_parameter);
      }
      // console.log("rates_from_bb: ",rates_from_bb)
      return {
        status: "200",
        response: rates_from_bb,
      };
    } else {
      console.log("Rates are not available.");
      return {
        status: rateData?.status,
        error: "Rates are not available for the given port pair.",
      };
    }

  }catch(error){
    dispatch(setOverlayProgress(100));
    console.error("Failed to fetch rates from GCP:", error.message);
    throw error;
  }
}

/**
 * This function ensure's that a webuser buyer has an entity that conforms to the new Breeze API updates
 *
 * @returns true if the user's entity has valid city, address, country, and country code fields, false otherwise
 */
export async function validateUserEntityForBreezeApi(isUserPresent, getstate) {
  const query = "mutation { action(id: $action_id input: $input) }";

  const action_id = "f4b61641e1034e5782ac232db1fff6bf";

  const variables = {
    action_id,
    input: {},
  };

  let jwt_token;
  if (isUserPresent) {
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  } else {
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN;
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  try {
    const response = await axios.post(BASE_API, { query, variables }, { headers });

    // we expect a successful run from apify for the user's entity
    const responseBody = response.data?.data?.action?.results?.data ?? {};

    const status = responseBody.status;
    const exitCode = responseBody.exitCode;
    const successfulResponse = status === "SUCCEEDED" && exitCode === 0;

    return successfulResponse;
  } catch (err) {
    return false;
  }
}

/**
 * The updateEntityLocation function calls the Betty endpoint to update an entity's
 * location parameters (address_1, city, country, countryCode) in order to comply with
 * the Breeze API updates (these fields are now mandatory)
 *
 * @param {Object} locationFields - object of the form address_1, city, country, countryCode (countryCode is a two letter code)
 * @param {Boolean} isUserPresent - true if there is a user logged in
 * @param {getstate} state of the CargoInsurance React form
 */
export async function updateEntityLocation(locationFields, isUserPresent, getstate) {
  const query = "mutation { action(id: $action_id input: $input) }";

  const action_id = "d0a3256fdeb747e1948cc4ebcad1b655";

  const variables = {
    action_id,
    input: {
      address_1: locationFields?.address_1,
      city: locationFields?.city,
      country: locationFields?.country,
      countryCode: locationFields?.countryCode,
    },
  };

  let jwt_token;
  if (isUserPresent) {
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  } else {
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN;
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  try {
    const response = await axios.post(BASE_API, { query, variables }, { headers });

    // the anticipated output is the entity with new fields
    const payload = response.data?.data?.action?.results ?? null;

    if (!payload) {
      throw new Error(response.data.data.errors[0]?.message);
    }
    return true;
  } catch (err) {
    throw err;
  } finally {
    return false;
  }
}
